import React, { useState, useEffect, useRef } from "react";
import confetti from "canvas-confetti";
import { playCelebrationSound } from "../../lib/celebrationSound";
import { useNavigate, useSearchParams } from "react-router-dom";
import styles from "./PaymentSuccess.module.css";
import useStepBackGuard from "../../hooks/useStepBackGuard";
import ExitConfirmModal from "../ExitConfirmModal/ExitConfirmModal";
import AnimatedButton from "../AnimatedButton/AnimatedButton";
import { getAgentById } from "../../Store/apiStore";
import { firePendingPurchase } from "../../lib/analytics";
import { markAgentPaid } from "../../lib/paymentGuard";

/*
 * PaymentSuccess — interstitial "Thank You" + How-billing-works + Setup
 * Progress screen shown right after a successful PAYG / assign-number
 * checkout, BEFORE the user lands on the real next screen (/go-live-setup).
 *
 * Inserted non-destructively: the checkout `successUrl` now points here
 * instead of straight to the next step. The "Continue" button forwards on
 * while preserving the original query params (ispayg, agentId, userId …) —
 * so no downstream functionality changes.
 *
 * Pure React (no platform-specific APIs) → works identically in the web
 * app and inside the Android/iOS WebView shell.
 */

// ── "How billing works" rows (matches approved mock) ──────────────────
const BILLING_ROWS = [
  {
    // Calendar
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path d="M7 2.5v3M17 2.5v3" stroke="#6524EB" strokeWidth="2" strokeLinecap="round" />
        <rect x="3" y="4.5" width="18" height="16" rx="3.5" fill="#6524EB" />
        <rect x="3" y="8.5" width="18" height="1.6" fill="#fff" opacity="0.85" />
        <g fill="#fff">
          <circle cx="8" cy="13" r="1.1" />
          <circle cx="12" cy="13" r="1.1" />
          <circle cx="16" cy="13" r="1.1" />
          <circle cx="8" cy="16.6" r="1.1" />
          <circle cx="12" cy="16.6" r="1.1" />
        </g>
      </svg>
    ),
    text: (
      <>
        Plan: <b>$5/month</b> (Business Number + PAYG)
      </>
    ),
  },
  {
    // Stopwatch / timer
    icon: (
     <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
<path fill-rule="evenodd" clip-rule="evenodd" d="M8.37692 2.00403C9.51493 2.09696 10.5837 2.43667 11.5295 2.96937L12.1141 2.29221C12.1954 2.19853 12.3342 2.18762 12.4283 2.26926L13.7724 3.4321C13.865 3.51223 13.8755 3.65331 13.7954 3.74623L13.2928 4.32859C14.6463 5.72243 15.48 7.62338 15.48 9.71771C15.48 13.9891 12.0114 17.4577 7.74001 17.4577C3.46859 17.4577 0 13.9891 0 9.71771C0 5.66073 3.12888 2.32832 7.10309 2.00403V1.27382H6.292C6.16936 1.27382 6.06929 1.17375 6.06929 1.05111V0.222712C6.06929 0.10007 6.16936 0 6.292 0H9.18801C9.31065 0 9.41072 0.10007 9.41072 0.222712V1.05111C9.41072 1.17375 9.31065 1.27382 9.18801 1.27382H8.37692V2.00403ZM7.74001 3.12437C4.09648 3.12437 1.14667 6.07418 1.14667 9.71771C1.14667 13.3612 4.09648 16.311 7.74001 16.311C11.3835 16.311 14.3333 13.3612 14.3333 9.71771C14.3333 6.07418 11.3835 3.12437 7.74001 3.12437Z" fill="#5F33E1"/>
<path fill-rule="evenodd" clip-rule="evenodd" d="M7.72352 9.71961V3.83203C4.47162 3.83203 1.83594 6.46809 1.83594 9.71961C1.83594 12.9711 4.47162 15.6072 7.72352 15.6072C9.70536 15.6072 11.554 14.6099 12.6431 12.9538L7.72352 9.71961Z" fill="#5F33E1"/>
</svg>

    ),
    text: (
      <>
        <b>50 free call minutes</b> to test it on real calls.
      </>
    ),
  },
  {
    // Phone handset
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
          d="M6.6 3.2h2.6l1.4 3.7-1.9 1.4a12 12 0 0 0 5 5l1.4-1.9 3.7 1.4v2.6a2 2 0 0 1-2.1 2A16 16 0 0 1 4.6 5.3 2 2 0 0 1 6.6 3.2z"
          fill="#6524EB"
        />
      </svg>
    ),
    text: (
      <>
        After that, <b>$0.48/min</b> on top of your <b>$5/month</b>.
      </>
    ),
  },
  {
    // Bell
    icon: (
      <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
        <path
          d="M12 3.5a5 5 0 0 1 5 5c0 3.9 1.3 5.2 2.2 6.1.55.55.16 1.5-.62 1.5H5.42c-.78 0-1.17-.95-.62-1.5C5.7 13.7 7 12.4 7 8.5a5 5 0 0 1 5-5z"
          fill="#6524EB"
        />
        <path d="M10 18.6a2 2 0 0 0 4 0" fill="#6524EB" />
        <path d="M3.4 7.2c.55-1.4 1.5-2.6 2.7-3.5M20.6 7.2c-.55-1.4-1.5-2.6-2.7-3.5" stroke="#6524EB" strokeWidth="1.6" strokeLinecap="round" />
      </svg>
    ),
    text: <>We'll alert you at <b>40</b> and <b>50</b> minutes, so no surprises</>,
  },
];

// ── Setup progress steps (matches approved mock) ──────────────────────
const SETUP_STEPS = [
  { title: "Business info confirmed", done: true },
  { title: "Test call completed", done: true },
  { title: "Plan: PAYG activated", done: true },
  { title: "AI Local phone number", done: false },
  { title: "Business call forwarding", done: false },
  { title: "Internal Call Triage", done: false },
];

const TOTAL_STEPS = SETUP_STEPS.length; // 6
const COMPLETED_STEPS = SETUP_STEPS.filter((s) => s.done).length; // 3
const PROGRESS_PCT = Math.round((COMPLETED_STEPS / TOTAL_STEPS) * 100);

const PaymentSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // GA4 first-purchase: this is the thank-you screen right after Stripe
  // checkout. Fire the stashed purchase once (the helper clears the stash,
  // so a refresh won't re-fire and the GoLiveWizard mount won't double-fire).
  useEffect(() => {
    firePendingPurchase();
  }, []);

  // Payment safal → confetti. Beech se ek bada burst, phir 250ms baad dono
  // kinaron se ek-ek (GoLiveCelebration jaisa), kul ~2s.
  // Ek payment pe ek hi baar: is success URL ke liye sessionStorage me nishaan
  // rakhte hain, taaki page refresh karne par dobara na chale. Ref isliye ki
  // dev (StrictMode) me effect do baar chalta hai.
  const confettiFiredRef = useRef(false);
  useEffect(() => {
    if (confettiFiredRef.current) return undefined;
    confettiFiredRef.current = true;
    const key = `paymentConfetti:${window.location.search}`;
    try {
      if (sessionStorage.getItem(key)) return undefined;
      sessionStorage.setItem(key, "1");
    } catch {
      /* storage blocked — confetti phir bhi chale */
    }
    const colors = ["#6524EB", "#22C55E", "#F97316", "#3B82F6", "#EC4899", "#A855F7"];
    // zIndex: page ke fixed footer / modals ke upar; canvas clicks nahi rokta.
    const base = { colors, zIndex: 100000, disableForReducedMotion: false };
    confetti({ ...base, particleCount: 110, spread: 80, startVelocity: 42, origin: { x: 0.5, y: 0.35 } });
    // Saath me chhoti celebration awaaz (browser ijazat de to — lib ka note dekho).
    playCelebrationSound();
    // Timer ko cleanup me nahi rokte: dev (StrictMode) me cleanup turant chal
    // jaata hai aur kinaron wale burst kat jaate. Der se chalna nuksaan nahi.
    setTimeout(() => {
      confetti({ ...base, particleCount: 60, angle: 60, spread: 60, origin: { x: 0, y: 0.6 } });
      confetti({ ...base, particleCount: 60, angle: 120, spread: 60, origin: { x: 1, y: 0.6 } });
    }, 250);
    return undefined;
  }, []);

  // Agent display name for the bottom hint. Seed from the cached
  // agentDetails (set during onboarding) so it paints instantly, then
  // confirm against the backend using the agentId from the success URL
  // — covers flows (e.g. PAYG) where sessionStorage isn't populated yet.
  const [agentName, setAgentName] = useState(() => {
    try {
      const a = JSON.parse(sessionStorage.getItem("agentDetails") || "null");
      return a?.agent?.agentName || a?.agent?.name || "Sophia";
    } catch {
      return "Sophia";
    }
  });

  useEffect(() => {
    const agentId =
      searchParams.get("agentId") || sessionStorage.getItem("SelectAgentId");
    if (!agentId) return undefined;
    // Payment succeeded → mark this agent as just-paid so back-navigating to a
    // plan/checkout screen can't trigger a DUPLICATE payment.
    markAgentPaid(agentId);
    let alive = true;
    (async () => {
      try {
        const res = await getAgentById(agentId);
        const a = res?.data || res || {};
        const name = a?.agentName || a?.agent?.agentName || a?.name;
        if (alive && name) setAgentName(name);
      } catch (err) {
        // Keep the sessionStorage / "Sophia" fallback on failure.
        console.warn("[PaymentSuccess] agent name fetch failed:", err?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [searchParams]);
  // Setup-progress accordion inside the sticky footer. Expands UPWARD
  // (footer is bottom-anchored) to reveal the step checklist.
  const [progressOpen, setProgressOpen] = useState(false);

  // ── Back guard ───────────────────────────────────────────────────
  // This is the first screen after Stripe checkout, so the (expired)
  // Stripe page sits right behind it in history. Any Back → ask to
  // confirm, then exit to the dashboard instead of the dead page.
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);
  useStepBackGuard({
    canGoBack: () => false,
    onExit: () => setExitConfirmOpen(true),
  });

  // Forward to the ORIGINAL destination, preserving every query param the
  // checkout success URL carried (fromCheckout / ispayg / agentId / userId /
  // upgraded / trial …). The destination route is passed as `next`
  // (defaults to /go-live-setup). Keeping ispayg/fromCheckout intact is what
  // lets GoLiveWizard's PAYG-Extra activation + post-checkout logic still run
  // exactly as before — so no flow (web or Android WebView) breaks.
  const handleContinue = () => {
    const params = new URLSearchParams(searchParams);
    const next = params.get("next") || "/go-live-setup";
    params.delete("next");
    const qs = params.toString();
    navigate(`${next}${qs ? `?${qs}` : ""}`, { replace: true });
  };

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        {/* Header — badge on the left, headings on the right */}
        <div className={styles.headerRow}>
          <div className={styles.badgeWrap}>
            <span className={`${styles.spark} ${styles.spark1}`} />
            <span className={`${styles.spark} ${styles.spark2}`} />
            <span className={`${styles.spark} ${styles.spark3}`} />
            <span className={`${styles.spark} ${styles.spark4}`} />
            <span className={`${styles.spark} ${styles.spark5}`} />
            <span className={`${styles.spark} ${styles.spark6}`} />
            <div className={styles.checkCircle}>
              <svg viewBox="0 0 24 24" width="28" height="28" aria-hidden="true">
                <path
                  d="M20 6L9 17l-5-5"
                  fill="none"
                  stroke="#fff"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>

          <div className={styles.headerText}>
            <h1 className={styles.title}>Thank You!</h1>
            <p className={styles.subTitle}>Your payment is successful.</p>
          </div>
        </div>

        <p className={styles.desc}>
          We've sent you a confirmation email with all the details you need.
        </p>

        {/* How billing works card */}
        <div className={styles.card}>
          <h2 className={styles.billingTitle}>How billing works</h2>
          <div className={styles.billingList}>
            {BILLING_ROWS.map((row, i) => (
              <div className={styles.billingRow} key={i}>
                <span className={styles.billingIcon}>{row.icon}</span>
                <span className={styles.billingVLine} aria-hidden="true" />
                <p className={styles.billingText}>{row.text}</p>
              </div>
            ))}
          </div>
        </div>

      </div>

      {/* Sticky bottom CTA + Setup-progress accordion (expands upward) */}
      <div className={styles.footer}>
        <div className={styles.footerInner}>
          {/* Expandable step checklist — sits ABOVE the header so it grows
              upward when opened. */}
          <div
            className={`${styles.accordionBody} ${progressOpen ? styles.accordionBodyOpen : ""
              }`}
          >
            <div className={styles.steps}>
              {SETUP_STEPS.map((step, i) => (
                <div
                  className={`${styles.stepRow} ${step.done ? styles.stepRowDone : styles.stepRowPending
                    }`}
                  key={i}
                >
                  <div className={styles.stepIconWrap}>
                    {step.done ? (
                      <span className={`${styles.stepIcon} ${styles.iconDone}`}>
                        <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true">
                          <path
                            d="M20 6L9 17l-5-5"
                            fill="none"
                            stroke="#fff"
                            strokeWidth="3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </span>
                    ) : (
                      <span className={`${styles.stepIcon} ${styles.iconPending}`}>
                        <svg width="10" height="12" viewBox="0 0 10 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M9.23379 11.0776C9.08644 8.66564 7.49018 7.31898 5.8055 6.10607C7.53929 4.85626 9.17976 3.46349 9.24361 0.922367H10V0H0V0.922367H0.756385C0.820236 3.46349 2.46071 4.85626 4.1945 6.10607C2.50982 7.31898 0.913556 8.66564 0.766208 11.0776H0V12H10V11.0776H9.23379ZM5 6.68716C6.62574 7.84935 8.08939 9.00692 8.25147 11.0776H1.74853C1.91061 9.00692 3.37426 7.84935 5 6.68716Z" fill="white" />
                        </svg>

                      </span>
                    )}
                  </div>

                  <span className={styles.stepTitle}>{step.title}</span>

                  <span
                    className={`${styles.badge} ${step.done ? styles.badgeDone : styles.badgePending
                      }`}
                  >
                    {step.done ? "COMPLETED" : "PENDING"}
                  </span>

                  {i < SETUP_STEPS.length - 1 && (
                    <span
                      className={`${styles.connector} ${SETUP_STEPS[i + 1].done
                          ? styles.connectorDone
                          : styles.connectorPending
                        }`}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Always-visible header — title + count + chevron toggle. */}
          <button
            type="button"
            className={styles.accordionHeader}
            onClick={() => setProgressOpen((o) => !o)}
            aria-expanded={progressOpen}
          >
            <span className={styles.cardTitle}>Setup Progress</span>
            <span className={styles.cardCount}>
              <b>{COMPLETED_STEPS}</b> of <b>{TOTAL_STEPS}</b> completed
            </span>
            <span
              className={`${styles.accordionChevron} ${progressOpen ? styles.accordionChevronOpen : ""
                }`}
              aria-hidden="true"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none">
                <path
                  d="M6 15l6-6 6 6"
                  stroke="#1d1d28"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </button>

          {/* Progress bar — always visible (collapsed + expanded). */}
          <div className={styles.progressTrack}>
            <div
              className={styles.progressFill}
              style={{ width: `${PROGRESS_PCT}%` }}
            />
          </div>

          <div className={styles.footerDivider} aria-hidden="true" />

          <AnimatedButton
            label="Continue"
            onClick={handleContinue}
            positionType="relative"
            width="100%"
            bottom="0px"
          />

          <p className={styles.nextHint}>
            <b className={styles.nextHintStrong}>Next:</b> Get local phone Number
            for your <b className={styles.nextHintStrong}>{agentName}</b>
          </p>
        </div>
      </div>

      <ExitConfirmModal
        open={exitConfirmOpen}
        onStay={() => setExitConfirmOpen(false)}
        onLeave={() => {
          setExitConfirmOpen(false);
          navigate("/fast-agent-detail", { replace: true });
        }}
      />
    </div>
  );
};

export default PaymentSuccess;
